import { Box, Stack, SxProps, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { GREEN_600, GRAY_1000, GRAY_600, GRAY_900 } from 'constants/styling/theme'
import { PLAN_COPY_KEY, PLAN_ORDER, PlanCardVariant } from 'constants/subscription'
import { SubscriptionPeriod, SubscriptionPlanType } from 'models/user/userWorkspaceDTOs'

import { MUISwitch } from 'components/common/MUISwitch'
import { PlanCard } from './PlanCard.component'
import { logAnalyticsEvent } from 'utils/analytics'
import { useGetTrialEligibility } from 'dataQueries/user.query'
import { usePlanPricing } from 'utils/hooks'
import { useTranslation } from 'react-i18next'

const getToggleLabelSx = (isActive: boolean): SxProps => ({
  color: isActive ? GRAY_1000 : GRAY_600,
  fontWeight: isActive ? 500 : 400,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'color 0.15s ease',
})

const planCardGridSx: SxProps = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '2.4rem',
  width: '100%',
}

interface Props {
  /** The workspace's active subscription plan */
  currentPlan: SubscriptionPlanType
  /** Callback when a plan card is selected */
  onSelectPlan: (plan: SubscriptionPlanType, period: SubscriptionPeriod) => void
  /** Whether to show the subtitle below the title */
  showSubtitle?: boolean
  /** Display variant: subscribe (pricing page) or manage (modal) */
  variant?: PlanCardVariant
}

export const ChoosePlan: FC<Props> = ({ currentPlan, onSelectPlan, showSubtitle = true, variant = PlanCardVariant.SUBSCRIBE }) => {
  const { t } = useTranslation(['pricing_page'])
  const [period, setPeriod] = useState<SubscriptionPeriod>(SubscriptionPeriod.YEARLY)

  const { data: trialEligibility } = useGetTrialEligibility()
  const trialAllowed = !!trialEligibility?.trialAllowed

  const { annualDiscount } = usePlanPricing()

  const isYearly = period === SubscriptionPeriod.YEARLY
  const currentPlanName = t(`pricing_page:plans.${PLAN_COPY_KEY[currentPlan]}.name`)
  const isManage = variant === PlanCardVariant.MANAGE

  /** Track the annual/monthly billing toggle as a funnel step (ignores redundant no-op clicks) */
  const handleSetPeriod = (newPeriod: SubscriptionPeriod) => {
    if (newPeriod === period) return

    logAnalyticsEvent('Toggle Billing Period', { period: newPeriod, currentPlan, trialAllowed, variant })
    setPeriod(newPeriod)
  }

  /** Track which plan/period the user selected before handing off to the parent flow */
  const handleSelectPlan = (planType: SubscriptionPlanType) => {
    logAnalyticsEvent('Select Plan', { planType, period, currentPlan, trialAllowed, variant })
    onSelectPlan(planType, period)
  }

  const toggleElement = (
    <Stack direction="row" alignItems="center" gap="1.2rem">
      <Typography
        variant="text-sm"
        sx={getToggleLabelSx(!isYearly)}
        onClick={() => handleSetPeriod(SubscriptionPeriod.MONTHLY)}
      >
        {t('pricing_page:monthly_label')}
      </Typography>
      <MUISwitch
        checked={isYearly}
        onChange={() => handleSetPeriod(isYearly ? SubscriptionPeriod.MONTHLY : SubscriptionPeriod.YEARLY)}
        size="small"
      />
      <Typography
        variant="text-sm"
        sx={getToggleLabelSx(isYearly)}
        onClick={() => handleSetPeriod(SubscriptionPeriod.YEARLY)}
      >
        {t('pricing_page:annual')}
      </Typography>

      {annualDiscount !== null && annualDiscount > 0 && (
        <Typography
          variant="text-sm"
          fontWeight={550}
          color={GREEN_600}
          sx={{ userSelect: 'none' }}
        >
          {t('pricing_page:annual_discount', { percent: annualDiscount })}
        </Typography>
      )}
    </Stack>
  )

  return (
    <Stack alignItems={isManage ? 'flex-start' : 'center'} gap="3.2rem" width="100%">
      {isManage && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="text-xl" fontWeight={500} color={GRAY_900}>
            {t('pricing_page:choose_your_plan')}
          </Typography>
          {toggleElement}
        </Stack>
      )}

      {!isManage && (
        <>
          <Stack alignItems="flex-start" gap="0.8rem" textAlign="center" width="100%">
            <Typography variant="text-xl" fontWeight={550} color={GRAY_900}>
              {t('pricing_page:choose_your_plan')}
            </Typography>
            {showSubtitle && (
              <Typography variant="text-sm" color={GRAY_600}>
                {t('pricing_page:current_plan_subtitle', { plan: currentPlanName })}
              </Typography>
            )}
          </Stack>
          {toggleElement}
        </>
      )}

      <Box sx={planCardGridSx}>
        {PLAN_ORDER.map((planType) => (
          <PlanCard
            key={planType}
            planType={planType}
            period={period}
            onSelect={() => handleSelectPlan(planType)}
            variant={variant}
            currentPlan={currentPlan}
            trialAllowed={trialAllowed}
          />
        ))}
      </Box>
    </Stack>
  )
}
